import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import api from '../services/api';

// ============================================
// CONFIGURAÇÃO DE TIMEOUT POR INATIVIDADE
// ============================================
// 1 hora e meia = 90 minutos = 5.400.000 ms
const INACTIVITY_TIMEOUT = 90 * 60 * 1000; // 1 hora e meia
const WARNING_BEFORE_LOGOUT = 60 * 1000; // Aviso 1 minuto antes do logout
// ============================================

// Actions types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  BLOCK_USER: 'BLOCK_USER',
  UPDATE_BLOCK_TIME: 'UPDATE_BLOCK_TIME',
  SHOW_INACTIVITY_WARNING: 'SHOW_INACTIVITY_WARNING',
  HIDE_INACTIVITY_WARNING: 'HIDE_INACTIVITY_WARNING'
};

// Estado inicial
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  loginAttempts: 0,
  isBlocked: false,
  blockUntil: 0,  // Timestamp de quando o bloqueio termina
  blockTimeRemaining: 0,
  // Flag para troca de senha obrigatória
  deveTrocarSenha: false,
  // Estados para inatividade
  showInactivityWarning: false,
  inactivityCountdown: 0,
  logoutReason: null // 'inactivity', 'manual', 'token_expired'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        loginAttempts: 0,
        isBlocked: false,
        blockUntil: 0,
        blockTimeRemaining: 0,
        deveTrocarSenha: action.payload.deveTrocarSenha || false
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      // O bloqueio é controlado pelo backend (status 423)
      // Frontend apenas exibe o erro e incrementa contador visual
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
        loginAttempts: action.payload.tentativasRestantes !== undefined 
          ? (3 - action.payload.tentativasRestantes) 
          : state.loginAttempts + 1
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
        logoutReason: action.payload?.reason || null
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        user: action.payload.user
      };
    
    case AUTH_ACTIONS.BLOCK_USER:
      const blockDuration = action.payload.blockTime || 15 * 60 * 1000; // 15 minutos default
      const blockUntilTime = Date.now() + blockDuration;
      return {
        ...state,
        isBlocked: true,
        blockUntil: blockUntilTime,
        blockTimeRemaining: blockDuration
      };
    
    case AUTH_ACTIONS.UPDATE_BLOCK_TIME:
      const remainingTime = Math.max(0, state.blockUntil - Date.now());
      return {
        ...state,
        blockTimeRemaining: remainingTime,
        isBlocked: remainingTime > 0
      };
    
    case AUTH_ACTIONS.SHOW_INACTIVITY_WARNING:
      return {
        ...state,
        showInactivityWarning: true,
        inactivityCountdown: action.payload.countdown
      };
    
    case AUTH_ACTIONS.HIDE_INACTIVITY_WARNING:
      return {
        ...state,
        showInactivityWarning: false,
        inactivityCountdown: 0
      };
    
    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  
  // Refs para controle de inatividade
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isWarningVisibleRef = useRef(false); // Usar ref para evitar re-renders

  // Função para fazer logout por inatividade
  const logoutDueToInactivity = useCallback(async () => {
    isWarningVisibleRef.current = false;
    
    // Limpar timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    try {
      const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      Cookies.remove('auth_token');
      Cookies.remove('refresh_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ 
        type: AUTH_ACTIONS.LOGOUT, 
        payload: { reason: 'inactivity' } 
      });
    }
  }, []);

  // Função para resetar o timer de inatividade (chamada apenas pelo botão ou inicialização)
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    isWarningVisibleRef.current = false;
    
    // Esconder aviso se estiver mostrando
    dispatch({ type: AUTH_ACTIONS.HIDE_INACTIVITY_WARNING });
    
    // Limpar timers existentes
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Tempo até mostrar o aviso (1 min - 30 seg = 30 seg)
    const timeUntilWarning = INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT;
    
    // Timer para mostrar aviso
    warningTimerRef.current = setTimeout(() => {
      isWarningVisibleRef.current = true;
      let countdown = Math.floor(WARNING_BEFORE_LOGOUT / 1000);
      
      dispatch({ 
        type: AUTH_ACTIONS.SHOW_INACTIVITY_WARNING, 
        payload: { countdown } 
      });
      
      // Countdown do aviso
      countdownIntervalRef.current = setInterval(() => {
        countdown -= 1;
        if (countdown <= 0) {
          clearInterval(countdownIntervalRef.current);
          logoutDueToInactivity();
        } else {
          dispatch({ 
            type: AUTH_ACTIONS.SHOW_INACTIVITY_WARNING, 
            payload: { countdown } 
          });
        }
      }, 1000);
      
    }, timeUntilWarning);
    
  }, [logoutDueToInactivity]);

  // Função para registrar atividade do usuário (NÃO reseta se modal estiver visível)
  const registerActivity = useCallback(() => {
    // Se o modal de aviso estiver visível, ignorar atividade
    if (isWarningVisibleRef.current) {
      return;
    }
    
    lastActivityRef.current = Date.now();
    
    // Limpar e reiniciar timer
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    const timeUntilWarning = INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT;
    
    warningTimerRef.current = setTimeout(() => {
      console.log('[Inactivity] Mostrando modal de aviso');
      isWarningVisibleRef.current = true;
      let countdown = Math.floor(WARNING_BEFORE_LOGOUT / 1000);
      
      dispatch({ 
        type: AUTH_ACTIONS.SHOW_INACTIVITY_WARNING, 
        payload: { countdown } 
      });
      
      countdownIntervalRef.current = setInterval(() => {
        countdown -= 1;
        if (countdown <= 0) {
          clearInterval(countdownIntervalRef.current);
          logoutDueToInactivity();
        } else {
          dispatch({ 
            type: AUTH_ACTIONS.SHOW_INACTIVITY_WARNING, 
            payload: { countdown } 
          });
        }
      }, 1000);
      
    }, timeUntilWarning);
    
  }, [logoutDueToInactivity]);

  // Monitorar atividade do usuário
  useEffect(() => {
    if (!state.isAuthenticated) {
      // Limpar timers se não autenticado
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      return;
    }
    
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart'
    ];
    
    // Debounce para não chamar em cada evento
    let debounceTimer = null;
    const handleActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        registerActivity();
      }, 500);
    };
    
    // Adicionar listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Iniciar timer
    resetInactivityTimer();
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (debounceTimer) clearTimeout(debounceTimer);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [state.isAuthenticated]); // Apenas depende de isAuthenticated

  // Função para manter a sessão ativa (quando usuário clica "Continuar")
  const keepSessionAlive = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Logout
  const logout = async () => {
    // Limpar timers de inatividade
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    try {
      const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
      
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Limpar tokens
      Cookies.remove('auth_token');
      Cookies.remove('refresh_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      
      // Limpar header da API
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: { reason: 'manual' } });
    }
  };

  // Função para tentar refresh do token
  const attemptRefreshToken = async () => {
    try {
      const refreshToken = Cookies.get('refresh_token') || localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('Refresh token não encontrado');
      }

      const response = await api.post('/auth/refresh-token', {
        refreshToken
      });

      const { token, user } = response.data;

      // Atualizar tokens
      Cookies.set('auth_token', token, { expires: 1 });
      localStorage.setItem('auth_token', token);

      // Configurar novo token no header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Atualizar estado
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token }
      });

      return { success: true, token };
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      // Se falhou, fazer logout
      logout();
      return { success: false, error: error.message };
    }
  };

  // Verificar token no localStorage/cookies na inicialização
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
        
        if (token) {
          // Configurar token no header da API
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verificar se o token é válido fazendo uma requisição para verificar
          try {
            const response = await api.get('/auth/verify');
            
            if (response.data.valid && response.data.user) {
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: {
                  user: response.data.user,
                  token
                }
              });
            } else {
              // Token inválido, remover
              Cookies.remove('auth_token');
              Cookies.remove('refresh_token');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('refresh_token');
              delete api.defaults.headers.common['Authorization'];
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            }
          } catch (error) {
            // Token inválido ou erro na verificação
            console.error('Token inválido:', error);
            Cookies.remove('auth_token');
            Cookies.remove('refresh_token');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            delete api.defaults.headers.common['Authorization'];
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        console.error('Erro na inicialização da autenticação:', error);
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initAuth();
  }, []);

  // Timer para atualizar tempo de bloqueio
  useEffect(() => {
    let interval;
    
    if (state.isBlocked && state.blockTimeRemaining > 0) {
      interval = setInterval(() => {
        dispatch({ type: AUTH_ACTIONS.UPDATE_BLOCK_TIME });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.isBlocked, state.blockTimeRemaining]);

  // Login
  const login = async (username, password, rememberMe = false) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      // Verificar se usuário está bloqueado
      if (state.isBlocked && state.blockTimeRemaining > 0) {
        const timeLeft = Math.ceil(state.blockTimeRemaining / 1000 / 60);
        throw new Error(`Usuário bloqueado. Tente novamente em ${timeLeft} minutos.`);
      }

      const response = await api.post('/auth/login', {
        username,
        password
      });

      const { token, refreshToken, user, deve_trocar_senha } = response.data;

      // Salvar tokens
      if (rememberMe) {
        Cookies.set('auth_token', token, { expires: 7, secure: true, sameSite: 'strict' });
        Cookies.set('refresh_token', refreshToken, { expires: 30, secure: true, sameSite: 'strict' });
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
      } else {
        Cookies.set('auth_token', token, { secure: true, sameSite: 'strict' });
        Cookies.set('refresh_token', refreshToken, { secure: true, sameSite: 'strict' });
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
      }

      // Configurar header da API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token, deveTrocarSenha: !!deve_trocar_senha }
      });

      // Retornar informação de deve_trocar_senha para o componente de login tratar
      return { success: true, user, deve_trocar_senha: !!deve_trocar_senha };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao fazer login';
      const status = error.response?.status;
      const responseData = error.response?.data;
      
      // Conta bloqueada pelo backend (status 423)
      if (status === 423) {
        // Usar o tempo restante que veio do backend, ou 15 minutos como fallback
        const timeRemaining = responseData?.time_remaining || 15 * 60 * 1000;
        
        dispatch({
          type: AUTH_ACTIONS.BLOCK_USER,
          payload: {
            error: errorMessage,
            blockTime: timeRemaining
          }
        });
        return { success: false, error: errorMessage };
      }
      
      // Tentativa de login inválida (401) - backend controla o bloqueio
      if (status === 401) {
        const tentativasRestantes = responseData?.tentativas_restantes;
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { 
            error: errorMessage,
            tentativasRestantes
          }
        });
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: errorMessage }
        });
      }

      return { success: false, error: errorMessage };
    }
  };

  // Esqueci minha senha
  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao solicitar reset de senha';
      return { success: false, error: errorMessage };
    }
  };

  // Redefinir senha
  const resetPassword = async (token, password) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao redefinir senha';
      return { success: false, error: errorMessage };
    }
  };

  // Verificar permissão
  const hasPermission = (permission) => {
    if (!state.user?.permissoes) return false;
    return state.user.permissoes.some(p => p.nome === permission);
  };

  // Verificar se tem qualquer uma das permissões
  const hasAnyPermission = (permissions) => {
    if (!state.user?.permissoes) return false;
    if (!Array.isArray(permissions)) return false;
    return permissions.some(permission => 
      state.user.permissoes.some(p => p.nome === permission)
    );
  };

  // Atualizar dados do usuário
  const refreshUser = async () => {
    try {
      const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
      
      if (!token) {
        return { success: false, error: 'Token não encontrado' };
      }

      const response = await api.get('/auth/verify');
      
      if (response.data.valid && response.data.user) {
        dispatch({
          type: AUTH_ACTIONS.REFRESH_TOKEN,
          payload: {
            user: response.data.user,
            token
          }
        });
        return { success: true, user: response.data.user };
      }

      return { success: false, error: 'Usuário inválido' };
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      return { success: false, error: error.message };
    }
  };

  // Limpar erro
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const value = {
    ...state,
    login,
    logout,
    forgotPassword,
    resetPassword,
    hasPermission,
    hasAnyPermission,
    clearError,
    attemptRefreshToken,
    refreshUser,
    keepSessionAlive, // Para manter sessão ativa quando usuário interagir com modal de aviso
    INACTIVITY_TIMEOUT,
    WARNING_BEFORE_LOGOUT
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export default AuthContext;
